import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { customerRequestOptions, newCustomerRequestId } from "../../services/customerService";

const API_BASE_URL = (process.env.REACT_APP_POS_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || "https://doticom.vn:3000").replace(/\/+$/, "");
const messageFor = error => error.response?.data?.message || error.message;
const points = value => Number(value || 0).toLocaleString("vi-VN");

export default function CustomerManagement() {
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [audit, setAudit] = useState([]);
  const [reason, setReason] = useState("");
  const [adjustment, setAdjustment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [syncPreview, setSyncPreview] = useState(null);
  const saving = useRef(false);
  const adjustmentRequest = useRef(null);
  const selectionRequest = useRef(0);

  const loadCustomers = useCallback(async signal => {
    setLoading(true);
    try {
      const result = await axios.get(`${API_BASE_URL}/api/customers/manage`, customerRequestOptions({
        params: { search: appliedSearch, page, pageSize: 50 }, signal,
      }));
      if (!signal?.aborted) setRows(result.data.data || []);
    } catch (err) {
      if (!signal?.aborted) setError(messageFor(err));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [appliedSearch, page]);

  useEffect(() => {
    const controller = new AbortController();
    loadCustomers(controller.signal);
    return () => controller.abort();
  }, [loadCustomers]);

  const openCustomer = async customer => {
    const request = ++selectionRequest.current;
    setError("");
    try {
      const result = await axios.get(`${API_BASE_URL}/api/customers/${encodeURIComponent(customer.MaKH)}/audit`, customerRequestOptions());
      if (request !== selectionRequest.current) return;
      const fresh = result.data.customer;
      setSelected(fresh);
      setForm({ Ten: fresh.Ten || "", SDT: fresh.SDT || "", DiaChi: fresh.DiaChi || "", MST: fresh.MST || "", Membership: fresh.Membership || "Classic" });
      setAudit(result.data.data || []);
      setReason("");
      setAdjustment("");
      adjustmentRequest.current = null;
    } catch (err) { if (request === selectionRequest.current) setError(messageFor(err)); }
  };

  const save = async kind => {
    if (!selected || saving.current) return;
    if (!reason.trim()) { setError("Vui lòng nhập lý do để lưu lịch sử thay đổi."); return; }
    const amount = Number(adjustment);
    if (kind === "points" && (!Number.isSafeInteger(amount) || amount === 0)) { setError("Điểm điều chỉnh phải là số nguyên khác 0."); return; }
    saving.current = true;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const url = `${API_BASE_URL}/api/customers/${encodeURIComponent(selected.MaKH)}`;
      if (kind === "profile") {
        await axios.put(url, { ...form, reason: reason.trim() }, customerRequestOptions());
      } else {
        const signature = JSON.stringify([selected.MaKH, amount, reason.trim()]);
        if (adjustmentRequest.current?.signature !== signature) adjustmentRequest.current = { signature, requestId: newCustomerRequestId() };
        await axios.post(`${url}/adjust-points`, { points: amount, reason: reason.trim(), requestId: adjustmentRequest.current.requestId }, customerRequestOptions());
      }
      setSuccess(kind === "profile" ? "Đã lưu hồ sơ khách hàng." : "Đã ghi nhận điều chỉnh điểm.");
      await openCustomer(selected);
      await loadCustomers();
    } catch (err) { setError(messageFor(err)); }
    finally { saving.current = false; setBusy(false); }
  };

  const sync = async dryRun => {
    if (saving.current) return;
    saving.current = true;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const result = await axios.post(`${API_BASE_URL}/api/customers/sync-from-db2`, { dryRun }, customerRequestOptions());
      setSyncPreview(result.data.data);
      if (!dryRun) { setSuccess(`Đã thêm ${result.data.data.insertedRows} khách hàng mới.`); await loadCustomers(); }
    } catch (err) { setError(messageFor(err)); }
    finally { saving.current = false; setBusy(false); }
  };

  return <main className="container-fluid py-4 px-3 px-lg-4" style={{ maxWidth: 1500 }}>
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
      <div><h1 className="h3 mb-1">Quản trị khách hàng</h1><p className="text-secondary mb-0">Mọi hạng thẻ hiện tích 2%. Mã khách hàng được giữ nguyên khi sửa hồ sơ.</p></div>
      <nav className="d-flex gap-3"><Link to="/admin">Quản lý</Link><Link to="/voucher-management">Voucher</Link></nav>
    </div>
    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    {success && <div className="alert alert-success" role="status">{success}</div>}
    <div className="card mb-3"><div className="card-body">
      <div className="d-flex flex-wrap align-items-end gap-2">
        <form className="d-flex flex-grow-1 gap-2" onSubmit={event => { event.preventDefault(); setError(""); setPage(1); setAppliedSearch(search.trim()); }}>
          <label className="flex-grow-1">Tìm tên, mã khách hàng hoặc số điện thoại
            <input className="form-control mt-1" value={search} onChange={event => setSearch(event.target.value)} placeholder="Hỗ trợ đầu số cũ/mới và +84" />
          </label><button className="btn btn-primary align-self-end" disabled={loading}>Tìm kiếm</button>
        </form>
        <button className="btn btn-outline-secondary" disabled={busy} onClick={() => sync(true)}>Xem trước đồng bộ từ IPOS</button>
      </div>
      {syncPreview && <div className="border rounded p-3 mt-3">
        <p className="mb-2">Có {syncPreview.candidateRows} khách hàng mới; {syncPreview.conflictRows || 0} xung đột cần đối soát; {syncPreview.skippedDuplicateSourceRows || 0} dòng nguồn trùng.</p>
        <p className="small text-secondary">Đồng bộ chỉ thêm khách mới, giữ nguyên hồ sơ và điểm đang có. Hồ sơ trùng cần được đối soát riêng.</p>
        {!!syncPreview.conflicts?.length && <details className="mb-2"><summary>Xem xung đột</summary><ul>{syncPreview.conflicts.map(row => <li key={`${row.MaKH}:${row.SDT}`}>{row.MaKH} · {row.SDT}</li>)}</ul></details>}
        {syncPreview.dryRun && <button className="btn btn-outline-primary" disabled={busy || !syncPreview.candidateRows} onClick={() => sync(false)}>Đồng bộ {syncPreview.candidateRows} khách hàng mới</button>}
      </div>}
    </div></div>
    <div className="row g-3">
      <section className={selected ? "col-12 col-xl-6" : "col-12"} aria-label="Danh sách khách hàng">
        <div className="card"><div className="table-responsive"><table className="table table-hover align-middle mb-0">
          <thead><tr><th>Mã KH</th><th>Khách hàng</th><th>Số điện thoại</th><th>Hạng</th><th></th></tr></thead>
          <tbody>{rows.map(row => <tr key={row.id}><td>{row.MaKH}</td><td>{row.Ten}</td><td>{row.SDT}</td><td>{row.Membership || "Classic"}</td><td><button className="btn btn-sm btn-outline-primary" disabled={busy} onClick={() => openCustomer(row)}>Chi tiết</button></td></tr>)}</tbody>
        </table></div>
          {!rows.length && <p className="p-3 mb-0">{loading ? "Đang tải…" : "Không có khách hàng phù hợp."}</p>}
          <div className="card-footer d-flex justify-content-between align-items-center"><button className="btn btn-sm btn-outline-secondary" disabled={page <= 1 || loading} onClick={() => setPage(value => value - 1)}>Trang trước</button><span>Trang {page}</span><button className="btn btn-sm btn-outline-secondary" disabled={rows.length < 50 || loading} onClick={() => setPage(value => value + 1)}>Trang sau</button></div>
        </div>
      </section>
      {selected && form && <section className="col-12 col-xl-6" aria-label="Chi tiết khách hàng">
        <div className="card mb-3"><div className="card-body">
          <h2 className="h5">{selected.Ten} · {selected.MaKH}</h2>
          <p className={selected.DiemConlai < 0 ? "text-danger" : "text-primary"}>Số dư: <strong>{points(selected.DiemConlai)} điểm</strong>{selected.NeedsReview ? " · Cần đối soát" : ""}</p>
          <div className="row g-2">{[["Ten", "Tên khách hàng"], ["SDT", "Số điện thoại"], ["DiaChi", "Địa chỉ"], ["MST", "Mã số thuế"]].map(([key, label]) => <label key={key} className="col-12 col-md-6">{label}<input className="form-control" value={form[key]} disabled={busy} onChange={event => setForm(value => ({ ...value, [key]: event.target.value }))} /></label>)}</div>
          <label className="d-block mt-2">Hạng thẻ<select className="form-select" value={form.Membership} disabled={busy} onChange={event => setForm(value => ({ ...value, Membership: event.target.value }))}>{["Classic", "Gold", "Diamond", "1"].map(tier => <option key={tier} value={tier}>{tier === "1" ? "Nhân viên" : tier}</option>)}</select></label>
          <label className="d-block mt-2">Lý do thay đổi<textarea className="form-control" value={reason} maxLength={255} disabled={busy} onChange={event => setReason(event.target.value)} /></label>
          <button className="btn btn-primary mt-3" disabled={busy} onClick={() => save("profile")}>Lưu hồ sơ</button>
          <hr />
          <label className="d-block">Điểm điều chỉnh (số dương để cộng, số âm để trừ)<input type="number" step="1" className="form-control" value={adjustment} disabled={busy} onChange={event => setAdjustment(event.target.value)} /></label>
          <button className="btn btn-outline-primary mt-2" disabled={busy} onClick={() => save("points")}>Ghi nhận điều chỉnh điểm</button>
        </div></div>
        <div className="card"><div className="card-body"><h2 className="h5">Lịch sử thay đổi</h2><p className="small text-secondary">100 thao tác gần nhất từ khi bật luồng mới. Lịch sử cũ được giữ để đối soát.</p></div>
          <div className="table-responsive"><table className="table table-sm mb-0"><thead><tr><th>Thời gian</th><th>Thao tác</th><th>Bill</th><th>Người thực hiện / Lý do</th><th>Số dư</th></tr></thead><tbody>{audit.map(row => <tr key={row.Id}><td>{new Date(row.CreatedAt).toLocaleString("vi-VN", { timeZone: "Asia/Ho_Chi_Minh" })}</td><td>{({ SALE: "Thanh toán", BILL_EDIT: "Sửa bill", BILL_VOID: "Hủy bill", ADJUSTMENT: "Điều chỉnh điểm", PROFILE: "Hồ sơ" })[row.OperationType]}</td><td>{row.IDXuat || "—"}</td><td>{row.Actor}<br /><small>{row.Reason}</small></td><td>{row.BalanceAfter == null ? "—" : points(row.BalanceAfter)}</td></tr>)}</tbody></table></div>
        </div>
      </section>}
    </div>
  </main>;
}
